import { useMemo, useState } from "react";
import { Check, Map, Search } from "lucide-react";
import { OfflineImage } from "@/components/common/offline-image/offline-image";
import { Input } from "@/components/ui/input";
import { isPlanArchived } from "@/lib/records/plan-utils";
import { cn } from "@/lib/utils";
import { planAssetKey } from "@/offline/asset-keys";
import type { ProjectPlan } from "@/types/api";
import { planDisplayName, planThumbnailUrl } from "./plan-display";

interface PlanPickerProps {
  plans: ProjectPlan[];
  selectedIds: string[];
  onToggle: (planId: string) => void;
  /** Sizing for the picker root — the host controls overall height. */
  className?: string;
}

/**
 * Search input + scrollable, multi-select list of project plans. The search
 * stays pinned at the top and the list is the only scroll region, so it is
 * keyboard-safe when hosted in the mobile full-screen picker.
 */
export function PlanPicker({
  plans,
  selectedIds,
  onToggle,
  className,
}: PlanPickerProps) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return plans;
    return plans.filter((plan) =>
      planDisplayName(plan).toLowerCase().includes(query),
    );
  }, [plans, search]);

  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  return (
    <div className={cn("flex flex-col", className)}>
      <div className="shrink-0 px-3 py-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            aria-label="Search plans"
            placeholder="Search plans…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pl-8"
          />
        </div>
      </div>
      <div
        className="min-h-0 flex-1 overflow-y-auto px-3"
        style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
      >
        {filtered.length === 0 ? (
          <p className="px-2 py-6 text-center text-sm text-muted-foreground">
            No matches
          </p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {filtered.map((plan) => {
              const isSelected = selected.has(plan.id);
              const archived = isPlanArchived(plan);
              const revision = plan.latest_revision;
              const thumbnail = planThumbnailUrl(plan);
              return (
                <li key={plan.id}>
                  <button
                    type="button"
                    onClick={() => onToggle(plan.id)}
                    aria-pressed={isSelected}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors",
                      isSelected
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-accent hover:text-accent-foreground",
                    )}
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
                      {thumbnail && revision ? (
                        <OfflineImage
                          assetKey={planAssetKey(
                            plan.id,
                            revision.id,
                            revision.tile_status,
                          )}
                          src={thumbnail}
                          alt=""
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <Map
                          className="h-4 w-4 text-muted-foreground"
                          aria-hidden="true"
                        />
                      )}
                    </div>
                    <span className="min-w-0 flex-1 truncate text-sm">
                      {planDisplayName(plan)}
                      {archived && (
                        <span className="ml-1 text-muted-foreground">
                          (Archived)
                        </span>
                      )}
                    </span>
                    {isSelected && (
                      <Check className="h-4 w-4 shrink-0 text-tertiary-ink" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
